import React, { useState } from 'react';
import { projectsData } from '../data/mock';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar, MapPin, ArrowRight, Filter } from 'lucide-react';

const ProjectsSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const categories = ['All', ...new Set(projectsData.projects.map(project => project.category))];
  
  const filteredProjects = selectedCategory === 'All' 
    ? projectsData.projects 
    : projectsData.projects.filter(project => project.category === selectedCategory);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="projects" className="py-10 bg-gradient-to-b from-gray-50/50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-[#0a8ec5]/10 border border-[#0a8ec5]/20 rounded-full px-4 py-2 mb-6">
            <span className="text-[#0a8ec5] font-semibold text-sm">Portfolio Showcase</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {projectsData.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {projectsData.subtitle}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <div className="flex items-center space-x-2 text-gray-600 mr-4">
            <Filter className="w-4 h-4" />
            <span className="font-medium">Filter by:</span>
          </div>
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setSelectedCategory(category)}
              variant={selectedCategory === category ? "default" : "outline"}
              className={`transition-all duration-200 ${
                selectedCategory === category 
                  ? 'bg-[#0a8ec5] hover:bg-[#0991d4] text-white' 
                  : 'border-[#0a8ec5]/30 text-[#0a8ec5] hover:bg-[#0a8ec5]/10'
              }`}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {filteredProjects.map((project) => (
            <Card key={project.id} className="group hover:shadow-2xl transition-all duration-300 hover:scale-105 border-0 bg-white overflow-hidden">
              {/* Project Image */}
              <div className="relative overflow-hidden">
                <div className="w-full h-48 bg-gradient-to-br from-[#0a8ec5]/10 to-[#cd2d31]/10 flex items-center justify-center">
                  <div className="text-center">                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#0a8ec5] transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-sm">
                        {project.description}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <Badge className="bg-[#cd2d31]/90 text-white border-0">
                    {project.category}
                  </Badge>
                </div>
                
                {/* Year Badge */}
                <div className="absolute top-4 right-4">
                  <Badge variant="outline" className="bg-white/90 border-gray-200 text-gray-700">
                    {project.year}
                  </Badge>
                </div>
                
                {/* Emission Info */}
                <div className="absolute bottom-4 right-4">
                  <Badge className="bg-[#0a8ec5]/90 text-white border-0">
                    <div className="flex items-center space-x-2">
                      <Filter className="w-3 h-3" />
                      <span>{project.emission}</span>
                    </div>
                  </Badge>
                </div>
                { /* Emission Badge */ }
                <div className="absolute bottom-4 left-4">
                  <Badge variant="outline" className="bg-white/90 border-gray-200 text-gray-700">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4" />
                      <span>{project.place}</span>
                    </div>
                  </Badge>
                </div>
              </div>
              
              {/* <CardContent className="p-6 space-y-4">
                
              </CardContent> */}
            </Card>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-[#0a8ec5]/5 to-[#cd2d31]/5 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Start Your Project?</h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Join our satisfied clients across industries. Let us design and implement the perfect air pollution control solution for your facility.
          </p>
          <Button 
            onClick={scrollToContact}
            className="bg-[#cd2d31] hover:bg-[#b02529] text-white px-8 py-3 text-lg font-semibold transition-all duration-200 hover:shadow-lg hover:scale-105"
          >
            Discuss Your Project
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;